/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from '@unform/web';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { addYears, endOfDay, startOfDay, subMonths } from 'date-fns';
import { useLocation } from 'react-router-dom';
import InputDate from '../InputDate';
import Select, { IOption } from '../Select';

import { Container } from './styles';
import Input from '../Input';
import { KrFilter } from '../KoroIcons';
import api from '~/services/api';
import InputMask from '../InputMask';

interface IFormData {
  [key: string]: any;
}

interface IFilters {
  onSubmitFilters(data: IFormData): void;
  event?: boolean;
  codeVoucher?: boolean;
  name?: boolean;
  document?: boolean;
  nameUser?: boolean;
  email?: boolean;
  city?: boolean;
  state?: boolean;
  score?: boolean;
  organizer?: boolean;
  sponsor?: boolean;
  category?: boolean;
  categories?: IOption[];
  modality?: boolean;
  modalities?: IOption[];
  status?: boolean;
  statusVouchersOptions?: IOption[];
  statusEvent?: boolean;
  statusCourtesy?: boolean;
  statusCheckIn?: boolean;
  statusPayment?: boolean;
  statusTicket?: boolean;
  order?: boolean;
  ticket?: boolean;
  request?: boolean;
  statusOptions?: IOption[];
  date?: boolean;
  notAllStatus?: boolean;
}

const FiltersLists: React.FC<IFilters> = ({
  onSubmitFilters,
  event,
  codeVoucher,
  name,
  document,
  nameUser,
  email,
  city,
  state,
  score,
  organizer,
  sponsor,
  category,
  categories: categoriesData,
  modality,
  modalities: modalitiesData,
  status,
  statusVouchersOptions,
  statusEvent,
  statusCourtesy,
  statusCheckIn,
  statusPayment,
  statusTicket,
  order,
  ticket,
  request,
  statusOptions,
  date,
  notAllStatus,
}) => {
  const location = useLocation();
  const formRef = useRef<FormHandles>(null);
  const [startDate, setStartDate] = useState<Date>(
    startOfDay(subMonths(new Date(), 6))
  );
  const [endDate, setEndDate] = useState<Date>(
    endOfDay(addYears(new Date(), 1))
  );
  const [categories, setCategories] = useState<IOption[]>([]);
  const [statusData, setStatusData] = useState<IOption[]>([]);
  const [modalities, setModalities] = useState<IOption[]>([]);
  const [events, setEvents] = useState<IOption[]>([]);
  const [eventName, setEventName] = useState('Todos');
  const [selectedEventId, setSelectedEventId] = useState<number | string>(-1);
  const [debouncedSearch, setDebouncedSearch] = useState(eventName);
  const [vouchersData, setVouchersData] = useState<IOption[]>([]);

  useEffect(() => {
    if (categoriesData) {
      setCategories([
        {
          id: 'todos',
          value: 'Todos',
          selected: true,
        },
        ...categoriesData.sort((a, b) => a.value.localeCompare(b.value)),
      ]);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (modalitiesData) {
      setModalities([
        {
          id: 'todas',
          value: 'Todas',
          selected: true,
        },
        ...modalitiesData.sort((a, b) => a.value.localeCompare(b.value)),
      ]);
    }
  }, [modalitiesData]);

  useEffect(() => {
    if (statusVouchersOptions) {
      setVouchersData([
        {
          id: 'all',
          value: 'Todos',
          selected: false,
        },
        ...statusVouchersOptions.sort((a, b) => a.value.localeCompare(b.value)),
      ]);
    }
  }, [statusVouchersOptions]);

  useEffect(() => {
    if (statusOptions) {
      if (notAllStatus) {
        setStatusData([
          ...statusOptions.sort((a, b) => a.value.localeCompare(b.value)),
        ]);
      } else {
        setStatusData([
          {
            id: 'todos',
            value: `${
              location.pathname === '/eventos' ? 'Todos (rodando)' : 'Todos'
            }`,
            selected: true,
          },
          ...statusOptions.sort((a, b) => a.value.localeCompare(b.value)),
        ]);
      }
    }
  }, [location.pathname, notAllStatus, statusOptions]);

  const handleLoadEvents = useCallback(
    async (search = '') => {
      const response = await api.get('events/organizer/dropdown', {
        params: {
          search: search.trim(),
          status_ids: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        },
      });

      const data = response.data.map((eventData: any) => ({
        id: eventData.id,
        value: `${eventData.name}`,
        selected: false,
      })) as IOption[];

      if (data.length === 0) {
        data.push({
          id: 0,
          value: 'Nenhum evento encontrado',
          selected: false,
          notSelectable: true,
        });
      }

      setEvents(() => {
        const list = [
          { id: -1, value: 'Todos', selected: selectedEventId === -1 },
          ...data,
        ];
        return list.map((item) => ({
          ...item,
          selected: item.id === selectedEventId,
        }));
      });
    },
    [selectedEventId]
  );

  useEffect(() => {
    handleLoadEvents(debouncedSearch);
  }, [debouncedSearch, handleLoadEvents]);

  useEffect(() => {
    const timer = setTimeout(() => {
      // só rebusca se o eventName for diferente do valor selecionado da lista
      const selectedOption = events.find((e) => e.id === selectedEventId);
      if (selectedOption && selectedOption.value === eventName) return;
      setDebouncedSearch(eventName === 'Todos' ? '' : eventName);
    }, 500);

    return () => clearTimeout(timer);
  }, [eventName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeTextEvent = useCallback((text) => {
    setEventName(text);
  }, []);

  return (
    <Container className="">
      <Form ref={formRef} onSubmit={onSubmitFilters}>
        <div className="bg-filter d-lg-flex">
          {!location.pathname.includes('/financeiro') && (
            <div className="text-end">
              <h2 className="h5 fw-semibold d-flex align-items-center mb-0">
                <KrFilter size={24} color="#000" className="me-2" />
                Filtros
              </h2>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn-clear mt-2 border-0 bg-transparent"
              >
                Limpar
              </button>
            </div>
          )}

          {name && (
            <div className="px-lg-2">
              <span className="d-block mb-1">
                {nameUser ? 'Comprador' : 'Nome ou Email'}
              </span>
              <Input className="height" name="name" type="text" />
            </div>
          )}

          {document && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Documento</span>
              <InputMask
                kind="custom"
                options={{ mask: '999.999.999-99' }}
                name="document"
                className="height"
              />
            </div>
          )}

          {ticket && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Inscrição</span>
              <Input name="ticket" className="height" />
            </div>
          )}

          {order && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Pedido</span>
              <Input name="order" className="height" />
            </div>
          )}

          {email && (
            <div className="px-lg-2">
              <span className="d-block mb-1">E-mail</span>
              <Input className="height" name="email" />
            </div>
          )}

          {city && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Cidade</span>
              <Input className="height" name="city" type="text" />
            </div>
          )}

          {state && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Estado</span>
              <Input className="height" name="state" type="text" />
            </div>
          )}

          {sponsor && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Patrocinador</span>
              <Input className="height" name="sponsor" type="text" />
            </div>
          )}

          {organizer && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Organizador</span>
              <Input className="height" name="organizer" type="text" />
            </div>
          )}

          {codeVoucher && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Cupom</span>
              <Input className="height" name="codeVoucher" type="text" />
            </div>
          )}

          {event && (
            <div className="w-20 px-lg-2">
              <span className="d-block mb-1">Evento</span>
              <Select
                name="event_id"
                className="bd-select"
                options={events.sort((a, b) => {
                  if (a.value === 'Todos') return -1;
                  if (b.value === 'Todos') return 1;
                  return a.value.localeCompare(b.value);
                })}
                onChangeText={handleChangeTextEvent}
                value={eventName}
                onChange={(e: any) => {
                  setSelectedEventId(e.id);
                  setEventName(e.value);
                  formRef.current?.setFieldValue('event-list', e.id);
                }}
              />
              <Input
                type="hidden"
                name="event"
                className="d-none"
                value={eventName.replace(/\s*\(\d+\)$/, '')}
              />
            </div>
          )}

          {request && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Pedido</span>
              <Input name="request" className="height" />
            </div>
          )}

          {statusEvent && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Status evento</span>
              <Select
                name="status"
                className="bd-select"
                options={statusData}
              />
            </div>
          )}

          {statusCheckIn && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Status Check-In</span>
              <Select
                name="status"
                className="bd-select"
                options={statusData}
              />
            </div>
          )}

          {statusPayment && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Status pagamento</span>
              <Select
                name="statusPayment"
                className="bd-select"
                options={statusData}
              />
            </div>
          )}

          {statusTicket && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Status</span>
              <Select
                name="statusTicket"
                className="bd-select"
                options={statusData}
              />
            </div>
          )}

          {statusCourtesy && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Status cortesia</span>
              <Select
                name="statusCourtesy"
                className="bd-select"
                options={statusData}
              />
            </div>
          )}

          {status && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Status</span>
              <Select
                name="status"
                className="bd-select"
                options={vouchersData}
              />
            </div>
          )}

          {score && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Score</span>
              <Select
                name="score"
                className="bd-select"
                options={[
                  {
                    id: 'all',
                    value: 'Score',
                    selected: true,
                  },
                  {
                    id: 'event1',
                    value: 'Evento1',
                    selected: false,
                  },
                  {
                    id: 'event2',
                    value: 'Evento2',
                    selected: false,
                  },
                ]}
              />
            </div>
          )}

          {category && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Esportes</span>
              <Select
                name="sports"
                className="bd-select mt-0"
                options={categories}
              />
            </div>
          )}

          {modality && (
            <div className="px-lg-2">
              <span className="d-block mb-1">Modalidade</span>
              <Select
                name="modality"
                className="bd-select mt-0"
                options={modalities}
              />
            </div>
          )}

          {date && (
            <>
              <div className="px-lg-2">
                {!location.pathname.includes('/financeiro') && (
                  <span className="mb-1 d-block">Data</span>
                )}

                <InputDate
                  name="dateStart"
                  selected={startDate}
                  colorSvg="#5D5D5D"
                  onChange={(e) => setStartDate(e as Date)}
                />
              </div>
              <div className="d-flex">
                <span className="small d-block mb-1 lh-lg mt-auto">Até</span>
              </div>
              <div className="px-lg-2 d-flex flex-column justify-content-end">
                <InputDate
                  name="dateEnd"
                  selected={endDate}
                  colorSvg="#5D5D5D"
                  className="mt-auto"
                  onChange={(e) => setEndDate(e as Date)}
                />
              </div>
            </>
          )}

          <button type="submit" className="btn-filter rounded px-4 py-1">
            Filtrar
          </button>
        </div>
      </Form>
    </Container>
  );
};

export default FiltersLists;
